import { Dictionary } from 'lodash';
import moment from 'moment';
import { User } from 'api/models';
import { TimeoffResponse } from 'api/timeoff/types';
import { Category } from 'api/timesheets/categories';
import { Configuration } from 'api/timesheets/configuration/configuration';
import { TimesheetReportSummary } from 'api/timesheets/reports/types';
import {
  DateRange,
  getValueFromDateRange,
} from 'components/organisms/projects-report/common/range-picker/types';
import {
  calculateUtilization,
  numberOfWorkingDaysBetweenDates,
} from 'components/organisms/timesheets/common/utils';
import {
  getCapacityInMinutesForADay,
  Minutes,
} from 'components/organisms/user-capacity';
import { getTimeOffArray } from 'utils/time-off-utils';
import { CategorySummary } from './types';
import { useCategorySummary } from './use-category-summary';

interface ReturnProps {
  billableMinutes: Minutes;
  nonBillableMinutes: Minutes;
  totalMinutes: Minutes;
  capacityInMinutes: Minutes;
  utilization: number;
  categoriesSummary: CategorySummary[];
  noOfDays: number;
  timeOffMinutes: number;
}

interface Props {
  summary: TimesheetReportSummary;
  dateRange: DateRange;
  timesheetCategories: Category[];
  configuration: Configuration;
  user?: User;
  userLookup?: Dictionary<User>;
}

export const useSummaryDetails = ({
  summary,
  dateRange,
  timesheetCategories,
  configuration,
  user,
  userLookup,
}: Props): ReturnProps => {
  const {
    billableMinutes,
    nonBillableMinutes,
    categories: _categories,
    users,
  } = summary;

  const [startingDate, endingDate] = getValueFromDateRange(dateRange);
  const noOfDays = numberOfWorkingDaysBetweenDates(
    startingDate,
    endingDate,
    configuration.workingDays,
  );
  const totalMinutes = billableMinutes + nonBillableMinutes;
  const weeklyCapacitOfAllUsersInHours =
    users?.reduce(
      (totalCapacity, { capacity: userCapacity }) =>
        totalCapacity + userCapacity,
      0,
    ) ??
    user?.capacity ??
    0;
  const dailyCapacityOfAllUsersInMinutes = getCapacityInMinutesForADay(
    weeklyCapacitOfAllUsersInHours,
    configuration.workingDays,
  );
  const { categoriesSummary } = useCategorySummary(
    _categories ?? [],
    timesheetCategories,
  );

  const getModifiedTimeOffArray = (timeOffArray: TimeoffResponse[]) => {
    return timeOffArray?.map((timeOff) => {
      let timeOffTemp = { ...timeOff };

      if (moment(timeOff.timeOffStart).isBefore(startingDate, 'day')) {
        timeOffTemp = {
          ...timeOffTemp,
          timeOffStart: String(startingDate),
        };
      }
      if (moment(timeOff.timeOffEnd).isAfter(endingDate, 'day')) {
        timeOffTemp = {
          ...timeOffTemp,
          timeOffEnd: String(endingDate),
        };
      }

      return timeOffTemp;
    });
  };

  const getTimeOffMinutesPerPerson = (
    modifiedTimeOffArray: TimeoffResponse[],
    userProp?: User,
  ) => {
    return modifiedTimeOffArray.reduce((timeOffMinutesT: number, timeOff) => {
      const countOfDays = numberOfWorkingDaysBetweenDates(
        moment(timeOff.timeOffStart).startOf('day'),
        moment(timeOff.timeOffEnd).endOf('day'),
        configuration.workingDays,
      );

      timeOffMinutesT +=
        countOfDays *
        getCapacityInMinutesForADay(
          userLookup?.[userProp?.userId ?? 0]?.capacity ?? 0,
          configuration.workingDays,
        );

      return timeOffMinutesT;
    }, 0);
  };

  const getTimeoffMinutesUserWise = () => {
    const timeOffArray = getTimeOffArray(
      startingDate,
      endingDate,
      userLookup?.[user?.userId ?? 0]?.userTimeOffDetails,
    );

    if (timeOffArray != null) {
      const modifiedTimeOffArray = getModifiedTimeOffArray(timeOffArray);

      return getTimeOffMinutesPerPerson(modifiedTimeOffArray, user);
    }

    return undefined;
  };

  const timeOffMinutes =
    // this is for overall summary calculation
    summary.users?.reduce((acc: number, userT) => {
      const timeOffArray = getTimeOffArray(
        startingDate,
        endingDate,
        userLookup?.[userT?.userId]?.userTimeOffDetails,
      );

      if (timeOffArray != null) {
        const modifiedTimeOffArray = getModifiedTimeOffArray(timeOffArray);

        const timeOffMinutesPerPerson = getTimeOffMinutesPerPerson(
          modifiedTimeOffArray,
          userT,
        );

        acc += timeOffMinutesPerPerson ?? 0;
      }

      return acc;
    }, 0) ??
    // this is for single user summary
    getTimeoffMinutesUserWise() ??
    0;

  const capacityInMinutes =
    dailyCapacityOfAllUsersInMinutes * noOfDays - timeOffMinutes;

  const utilization = calculateUtilization(billableMinutes, capacityInMinutes);

  return {
    billableMinutes,
    nonBillableMinutes,
    totalMinutes,
    capacityInMinutes,
    utilization,
    categoriesSummary,
    noOfDays,
    timeOffMinutes,
  };
};
